<template>
  <div>
    <div class="d-flex justify-space-between align-center flex-wrap gap-2 mb-4">
      <div>
        <h6 class="ma-0 mb-1">DRE</h6>
        <div class="text-caption text--secondary">
          {{ periodLabel }} · competência
        </div>
      </div>
    </div>

    <div class="d-flex align-center flex-wrap gap-2 mb-3">
      <v-text-field
        v-model="startDate"
        label="De"
        type="date"
        dense
        outlined
        hide-details
        style="max-width: 170px"
        @change="loadDre"
      />
      <v-text-field
        v-model="endDate"
        label="Até"
        type="date"
        dense
        outlined
        hide-details
        style="max-width: 170px"
        @change="loadDre"
      />
      <v-chip-group :value="activePreset" class="ma-0" @change="applyPreset">
        <v-chip
          v-for="preset in presets"
          :key="preset.id"
          :value="preset.id"
          small
          label
          filter
          outlined
        >
          {{ preset.label }}
        </v-chip>
      </v-chip-group>
      <v-btn icon small :loading="loading" @click="loadDre">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <v-alert v-if="error" type="error" dense text class="mb-3">
      {{ error }}
    </v-alert>

    <v-card outlined class="pa-3">
      <div v-if="loading && !statement.length" class="text-caption text--secondary">
        Carregando demonstrativo...
      </div>
      <div
        v-for="row in statement"
        :key="row.key"
        class="dre-row"
        :class="{
          'dre-subtotal': row.kind === 'subtotal',
          'dre-result': row.kind === 'result',
          'mt-4': row.excluded,
        }"
      >
        <div class="d-flex justify-space-between align-start">
          <div>
            <div :class="row.kind === 'group' ? '' : 'font-weight-bold'">
              {{ row.label }}
            </div>
            <small v-if="row.note" class="text--secondary">{{ row.note }}</small>
          </div>
          <div class="font-weight-medium text-no-wrap" :class="amountClass(lineAmount(row))">
            {{ lineAmount(row) | currency }}
          </div>
        </div>
        <div
          v-for="category in row.categories || []"
          :key="category.id || category.name"
          class="d-flex justify-space-between text-caption pl-4 py-1"
        >
          <span>{{ category.name }}</span>
          <span class="text-no-wrap" :class="amountClass(categoryAmount(row, category))">
            {{ categoryAmount(row, category) | currency }}
          </span>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import moment from "moment";
import CASH from "@/services/ultimate/cash.js";

export default {
  name: "CashDre",
  props: {
    companyId: {
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      startDate: moment().startOf("month").format("YYYY-MM-DD"),
      endDate: moment().endOf("month").format("YYYY-MM-DD"),
      statement: [],
      presets: [
        { id: "current", label: "Este mês" },
        { id: "previous", label: "Mês passado" },
        { id: "year", label: "Este ano" },
      ],
    };
  },
  computed: {
    periodLabel() {
      return `${moment(this.startDate).format("DD/MM/YYYY")} — ${moment(
        this.endDate
      ).format("DD/MM/YYYY")}`;
    },
    activePreset() {
      const start = this.startDate;
      const end = this.endDate;
      const currentStart = moment().startOf("month").format("YYYY-MM-DD");
      const currentEnd = moment().endOf("month").format("YYYY-MM-DD");
      if (start === currentStart && end === currentEnd) return "current";

      const prev = moment().subtract(1, "month");
      if (
        start === prev.clone().startOf("month").format("YYYY-MM-DD") &&
        end === prev.clone().endOf("month").format("YYYY-MM-DD")
      ) {
        return "previous";
      }

      if (
        start === moment().startOf("year").format("YYYY-MM-DD") &&
        end === moment().endOf("year").format("YYYY-MM-DD")
      ) {
        return "year";
      }

      return null;
    },
  },
  methods: {
    lineAmount(row) {
      const total = Number(row.total || 0);
      if (!total) return 0;
      if (row.kind === "group" && row.sign < 0) return -total;
      return total;
    },
    categoryAmount(row, category) {
      const amount = Number(category.amount || 0);
      if (!amount) return 0;
      if (row.sign < 0) return -amount;
      return amount;
    },
    amountClass(value) {
      if (value > 0) return "success--text";
      if (value < 0) return "error--text";
      return "text--secondary";
    },
    applyPreset(presetId) {
      if (!presetId) return;
      if (presetId === "current") {
        this.startDate = moment().startOf("month").format("YYYY-MM-DD");
        this.endDate = moment().endOf("month").format("YYYY-MM-DD");
      } else if (presetId === "previous") {
        const prev = moment().subtract(1, "month");
        this.startDate = prev.clone().startOf("month").format("YYYY-MM-DD");
        this.endDate = prev.clone().endOf("month").format("YYYY-MM-DD");
      } else if (presetId === "year") {
        this.startDate = moment().startOf("year").format("YYYY-MM-DD");
        this.endDate = moment().endOf("year").format("YYYY-MM-DD");
      }
      this.loadDre();
    },
    async loadDre() {
      try {
        this.loading = true;
        this.error = null;
        const params = {
          startDate: this.startDate,
          endDate: this.endDate,
        };
        if (this.companyId) params.companyId = this.companyId;
        const response = await CASH.getDre(params);
        this.statement = response.statement || [];
      } catch (error) {
        this.error = error.message || "Erro ao carregar o DRE";
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    companyId() {
      this.loadDre();
    },
  },
  mounted() {
    this.loadDre();
  },
};
</script>

<style scoped>
.dre-row {
  padding: 6px 0;
}

.dre-subtotal {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.dre-result {
  margin-top: 8px;
  padding-top: 10px;
  border-top: 2px solid rgba(0, 0, 0, 0.38);
}

.theme--dark .dre-subtotal {
  border-top-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .dre-result {
  border-top-color: rgba(255, 255, 255, 0.38);
}
</style>
